import { useCallback, useEffect, useRef, useState } from "react";
import { BOARD_LIMITS, type BoardMark, type ShoppingBoard } from "../../../shared/kitchen/board";
import { api, ApiRequestError } from "../../data/api";

const POLL_MS = 3000;
const RETRY_MS = 4000;

type Change = { kind: "marks"; marks: BoardMark[] } | { kind: "erase" | "restore"; ids: string[] };

/**
 * A list's shopping board (shared/kitchen/board.ts), kept in step with every other
 * screen the way the daily whiteboard is (src/whiteboard/useBoard.ts): a full load,
 * then only the changes every few seconds while it's open. Writing, stamping and
 * erasing show at once and are sent in order behind the scenes.
 */
export function useListBoard(listId: string) {
  const [marks, setMarks] = useState<Map<string, BoardMark>>(new Map());
  const [meta, setMeta] = useState<{ loaded: boolean; error: string | null; full: boolean }>({ loaded: false, error: null, full: false });
  /** Bumped when marks are removed or replaced, so the canvas redraws from scratch. */
  const [epoch, setEpoch] = useState(0);
  const rev = useRef(0);
  const queue = useRef<Change[]>([]);
  const sending = useRef(false);
  const listRef = useRef(listId);
  listRef.current = listId;

  useEffect(() => {
    let stopped = false;
    let timer = 0;
    rev.current = 0;
    setMarks(new Map());
    setEpoch((e) => e + 1);
    setMeta({ loaded: false, error: null, full: false });
    const pull = async () => {
      if (stopped) return;
      if (document.visibilityState === "visible" && !sending.current && !queue.current.length) {
        try {
          const res = await api<ShoppingBoard>(`/kitchen/lists/${listId}/board${rev.current ? `?since=${rev.current}` : ""}`);
          if (stopped) return;
          const full = !rev.current;
          rev.current = res.rev;
          if (full || res.marks.length || res.removed.length) {
            setMarks((prev) => {
              const next = full ? new Map<string, BoardMark>() : new Map(prev);
              for (const m of res.marks) next.set(m.id, m);
              for (const id of res.removed) next.delete(id);
              return next;
            });
            if (full || res.removed.length) setEpoch((e) => e + 1);
          }
          setMeta((m) => ({ ...m, loaded: true, error: null }));
        } catch (e) {
          if (!stopped) setMeta((m) => ({ ...m, error: e instanceof Error ? e.message : "Couldn't load the board" }));
        }
      }
      timer = window.setTimeout(pull, POLL_MS);
    };
    void pull();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [listId]);

  const flush = useCallback(async () => {
    if (sending.current) return;
    sending.current = true;
    try {
      while (queue.current.length) {
        const c = queue.current[0]!;
        const base = `/kitchen/lists/${listRef.current}/board`;
        try {
          if (c.kind === "marks") await api(`${base}/marks`, { method: "POST", body: { marks: c.marks } });
          else await api(`${base}/${c.kind}`, { method: "POST", body: { ids: c.ids } });
          queue.current.shift();
        } catch (e) {
          if (e instanceof ApiRequestError && (e.status === 409 || e.status === 400 || e.status === 404)) {
            // The board is full (or the list went away): stop sending.
            queue.current = [];
            setMeta((m) => ({ ...m, full: e.status === 409 }));
            break;
          }
          await new Promise((r) => setTimeout(r, RETRY_MS));
        }
      }
    } finally {
      sending.current = false;
    }
  }, []);

  const send = useCallback(
    (c: Change) => {
      const last = queue.current[queue.current.length - 1];
      // Marks made in quick succession travel together.
      if (c.kind === "marks" && last?.kind === "marks" && !sending.current && last.marks.length + c.marks.length <= BOARD_LIMITS.marksPerRequest) last.marks.push(...c.marks);
      else queue.current.push(c);
      void flush();
    },
    [flush],
  );

  const add = useCallback(
    (m: BoardMark) => {
      setMarks((prev) => new Map(prev).set(m.id, m));
      send({ kind: "marks", marks: [m] });
    },
    [send],
  );

  const erase = useCallback(
    (ids: string[]) => {
      if (!ids.length) return;
      setMarks((prev) => {
        const next = new Map(prev);
        for (const id of ids) next.delete(id);
        return next;
      });
      setEpoch((e) => e + 1);
      send({ kind: "erase", ids });
    },
    [send],
  );

  const restore = useCallback(
    (list: BoardMark[]) => {
      if (!list.length) return;
      setMarks((prev) => {
        const next = new Map(prev);
        for (const m of list) next.set(m.id, m);
        return next;
      });
      setEpoch((e) => e + 1);
      send({ kind: "restore", ids: list.map((m) => m.id) });
    },
    [send],
  );

  return { marks, epoch, ...meta, add, erase, restore };
}
